'use client';

import {
  type TooltipProps as HeroTooltipProps,
  Tooltip as HeroUiTooltip,
  useTooltip,
} from '@heroui/tooltip';
import { forwardRef } from 'react';
import { cn } from '@/utils';

export type TooltipProps = HeroTooltipProps & {
  /**
   * HeroUI mounts tooltip overlay only while open (`isOpen && …`), so images inside do not load until first hover.
   * When true, the same `content` is also rendered off-screen (CSS-hidden, still mounted) so assets load earlier.
   */
  keepContentMounted?: boolean;
};

export type { OverlayPlacement as TooltipPlacement } from '@heroui/aria-utils';

export const Tooltip = forwardRef<HTMLElement, TooltipProps>((props, ref) => {
  const { className, classNames, keepContentMounted, content, isDisabled, ...heroProps } = props;

  const prefetch =
    keepContentMounted && content != null && !isDisabled ? (
      <div
        aria-hidden
        className={cn(
          'pointer-events-none fixed top-0 -left-[10000px] z-[-1]',
          'max-h-[70dvh] max-w-[720px] overflow-hidden opacity-0',
        )}
        // Duplicate subtree for prefetch only — disable interaction / focus (nested links, buttons).
        inert
      >
        {content}
      </div>
    ) : null;

  return (
    <>
      {prefetch}
      <HeroUiTooltip
        ref={ref}
        {...heroProps}
        content={content}
        isDisabled={isDisabled}
        className={cn('!rounded-2xl', className)}
        classNames={{
          ...classNames,
          content: cn('!rounded-2xl', classNames?.content),
        }}
      />
    </>
  );
});
Tooltip.displayName = 'Tooltip';

export { useTooltip };
